import { EMPTY_TILE } from "../document/packed-tile.ts";
import { hashWords, toUnitFloat } from "../random.ts";
import type { CompiledRule, CompiledStage } from "./compile-rules.ts";

export { compileRuleStage } from "./compile-rules.ts";
export type { CompiledRule, CompiledStage } from "./compile-rules.ts";

/**
 * Applies compiled automapping rules to packed working layers, reproducing Tiled's `autoMap`, `matchRule`,
 * `applyRule` and `copyTileRegion` (ADR-010 sections 3 to 5). Probability draws use the seeded hash instead of
 * Tiled's unseeded generator.
 */

/**
 * The working map: one packed layer per target name. Layers are mutated in place; a missing input target
 * reads as empty and a missing output target is created.
 */
export interface WorkingMap {
  readonly width: number;
  readonly height: number;
  readonly layers: Map<string, Uint32Array>;
  readonly seed: number;
  readonly layerIndex: number;
  readonly stageIndex: number;
}

/**
 * A cell rectangle, `x0` and `y0` inclusive, `x1` and `y1` exclusive.
 */
export interface CellRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * A count of the work a stage run did: `tested` is the number of rule positions tried, one for every active
 * rule at every candidate position, and `drawn` the number of probability draws made, each one seeded hash.
 */
export interface StageTally {
  tested: number;
  drawn: number;
}

/**
 * Options for a restricted or instrumented stage run (editor incremental updates, ADR-020).
 */
export interface ApplyOptions {
  readonly rect?: CellRect;
  readonly snapshot?: ReadonlyMap<string, Uint32Array>;
  readonly written?: CellRect;
  readonly writeRect?: CellRect;
  readonly tally?: StageTally;
}

const HASH_INPUTS = 5;

/**
 * Reports whether a rule matches at a position against the given layers.
 *
 * @param rule Compiled rule.
 * @param px Position x of the input box.
 * @param py Position y of the input box.
 * @param width Map width.
 * @param layers Layers to read.
 * @returns True when every constraint holds.
 */
function matchesAt(rule: CompiledRule, px: number, py: number, width: number, layers: ReadonlyMap<string, Uint32Array>): boolean {
  for (const target of rule.targets) {
    const layer = layers.get(target.target);
    for (const constraint of target.constraints) {
      const value = layer === undefined ? EMPTY_TILE : (layer[(py + constraint.dy) * width + px + constraint.dx] as number);
      if (constraint.anyOf.length > 0) {
        let accepted = false;
        for (let i = 0; i < constraint.anyOf.length; i += 1) {
          if (constraint.anyOf[i] === value) {
            accepted = true;
            break;
          }
        }
        if (!accepted) {
          return false;
        }
      }
      for (let i = 0; i < constraint.noneOf.length; i += 1) {
        if (constraint.noneOf[i] === value) {
          return false;
        }
      }
    }
  }
  return true;
}

/**
 * Writes a rule's outputs at a position, clipped to the map.
 *
 * @param rule Compiled rule.
 * @param px Position x.
 * @param py Position y.
 * @param map Working map.
 * @param written Bounds accumulator grown by every cell whose value changes, or undefined.
 * @param clip Cells outside this rectangle are not written; the map rectangle for a normal run.
 */
function applyAt(rule: CompiledRule, px: number, py: number, map: WorkingMap, written: CellRect | undefined, clip: CellRect): void {
  for (const output of rule.outputs) {
    let layer = map.layers.get(output.target);
    if (layer === undefined) {
      layer = new Uint32Array(map.width * map.height).fill(EMPTY_TILE);
      map.layers.set(output.target, layer);
    }
    for (const cell of output.cells) {
      const x = px + cell.dx;
      const y = py + cell.dy;
      if (x >= clip.x0 && y >= clip.y0 && x < clip.x1 && y < clip.y1) {
        const index = y * map.width + x;
        if (written !== undefined && layer[index] !== cell.value) {
          written.x0 = Math.min(written.x0, x);
          written.y0 = Math.min(written.y0, y);
          written.x1 = Math.max(written.x1, x + 1);
          written.y1 = Math.max(written.y1, y + 1);
        }
        layer[index] = cell.value;
      }
    }
  }
}

/**
 * Reports whether the probability draw skips a rule at a position.
 *
 * @param rule Compiled rule.
 * @param px Position x.
 * @param py Position y.
 * @param map Working map, for the seed and indices.
 * @param scratch Preallocated hash input buffer of five words.
 * @param tally Grown by one when a draw is made, or undefined when the run is not counted.
 * @returns True when the position is skipped.
 */
export function skipped(rule: CompiledRule, px: number, py: number, map: WorkingMap, scratch: Uint32Array, tally: StageTally | undefined): boolean {
  if (rule.skipChance <= 0) {
    return false;
  }
  if (tally !== undefined) {
    tally.drawn += 1;
  }
  scratch[0] = px;
  scratch[1] = py;
  scratch[2] = map.layerIndex;
  scratch[3] = map.stageIndex;
  scratch[4] = rule.id;
  return toUnitFloat(hashWords(map.seed, scratch, HASH_INPUTS)) < rule.skipChance;
}

/**
 * Whether a stage will draw: a rule with a probability below 1 has a position to try in the run's rectangle.
 *
 * @param rules The stage's active rules.
 * @param x0 First candidate column.
 * @param y0 First candidate row.
 * @param rect The run's candidate rectangle.
 * @param map Working map, for its size.
 * @returns True when some drawing rule has a candidate position.
 */
function draws(rules: readonly CompiledRule[], x0: number, y0: number, rect: CellRect, map: WorkingMap): boolean {
  for (const rule of rules) {
    if (rule.skipChance > 0 && Math.min(rect.x1, map.width - rule.inputWidth + 1) > x0 && Math.min(rect.y1, map.height - rule.inputHeight + 1) > y0) {
      return true;
    }
  }
  return false;
}

/**
 * Applies one compiled stage to a working map in place.
 *
 * With `matchInOrder` false every rule is matched against the layers as they were when the stage started and
 * all matches are then applied in rule order. With `matchInOrder` true each rule is processed in turn and each
 * match is applied as soon as it is found, scanning rows top to bottom and columns left to right, so later
 * positions see earlier output.
 *
 * A rule's probability draw is made only at a position the rule matches. The draw is a pure function of the
 * seed, the position, the layer, the stage and the rule, so whether it is made before or after the match
 * decides nothing but the cost, and most positions match no rule.
 *
 * @param stage Compiled stage.
 * @param map Working map; its layers are mutated and new output targets added.
 * @param options `rect` restricts candidate positions to a cell rectangle (default the whole map);
 *   `snapshot` supplies the pre-stage layers a `MatchInOrder` false stage reads instead of copying the
 *   working layers; `written` accumulates the bounds of every cell whose value changed; `writeRect` drops
 *   writes outside a rectangle; `tally` is grown by the rule positions the run tries and the draws it makes.
 * @throws RangeError when a rule with a probability below 1 has a position to try and the map's seed is
 *   outside `[0, 2^32)`; nothing is written then.
 */
export function applyRuleStage(stage: CompiledStage, map: WorkingMap, options: ApplyOptions = {}): void {
  const scratch = new Uint32Array(HASH_INPUTS);
  const rules = stage.rules.filter((r) => !r.disabled && !r.impossible);
  const whole: CellRect = { x0: 0, y0: 0, x1: map.width, y1: map.height };
  const rect = options.rect ?? whole;
  const written = options.written;
  const clip = options.writeRect ?? whole;
  const tally = options.tally;
  const x0 = Math.max(0, rect.x0);
  const y0 = Math.max(0, rect.y0);
  if (draws(rules, x0, y0, rect, map)) {
    hashWords(map.seed, scratch, 0);
  }
  if (stage.matchInOrder) {
    for (const rule of rules) {
      const yEnd = Math.min(rect.y1, map.height - rule.inputHeight + 1);
      const xEnd = Math.min(rect.x1, map.width - rule.inputWidth + 1);
      if (tally !== undefined) {
        tally.tested += Math.max(0, xEnd - x0) * Math.max(0, yEnd - y0);
      }
      for (let py = y0; py < yEnd; py += 1) {
        for (let px = x0; px < xEnd; px += 1) {
          if (matchesAt(rule, px, py, map.width, map.layers) && !skipped(rule, px, py, map, scratch, tally)) {
            applyAt(rule, px, py, map, written, clip);
          }
        }
      }
    }
    return;
  }
  let snapshot = options.snapshot;
  if (snapshot === undefined) {
    const copy = new Map<string, Uint32Array>();
    for (const [name, layer] of map.layers) {
      copy.set(name, new Uint32Array(layer));
    }
    snapshot = copy;
  }
  for (const rule of rules) {
    const positions: number[] = [];
    const yEnd = Math.min(rect.y1, map.height - rule.inputHeight + 1);
    const xEnd = Math.min(rect.x1, map.width - rule.inputWidth + 1);
    if (tally !== undefined) {
      tally.tested += Math.max(0, xEnd - x0) * Math.max(0, yEnd - y0);
    }
    for (let py = y0; py < yEnd; py += 1) {
      for (let px = x0; px < xEnd; px += 1) {
        if (matchesAt(rule, px, py, map.width, snapshot) && !skipped(rule, px, py, map, scratch, tally)) {
          positions.push(px, py);
        }
      }
    }
    for (let i = 0; i < positions.length; i += 2) {
      applyAt(rule, positions[i] as number, positions[i + 1] as number, map, written, clip);
    }
  }
}
